import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Car, ArrowRight, ChevronDown, ChevronUp, Loader2, Plus, Trash2, X } from 'lucide-react';
import CityInput from '../../CityInput';
import { useTranslation } from '../../useTranslation';
import type { CarTrip, CarTripDayCreate } from '../../types/CarTripTypes';
import { getAllTripTags } from '../../services/api/flightsApi';
import { getDistanceByCities } from '../../services/api/geocodingApi';
import type { CarTripDraft } from '../../services/CarTripHookService';
import {
    addDraftDay,
    createDraftDay,
    removeDraftDay,
    updateDraftDay,
    type CarTripDayDraft,
} from '../../utils/carTripUi';
import { CarRouteSep } from './CarRouteSep';
import { Checkbox } from '../common/Checkbox';
import { MenuSelect } from '../common/MenuSelect';
import { TagCombobox } from '../common/TagCombobox';

type InvalidFields = Record<string, boolean>;

export const CarTripForm: React.FC<{
    showAddCarTrip: boolean;
    setShowAddCarTrip: (show: boolean) => void;
    newCarTrip: CarTripDraft;
    setNewCarTrip: React.Dispatch<React.SetStateAction<CarTripDraft>>;
    addCarTrip: () => void;
    updateCarTrip?: () => void;
    isEditing?: boolean;
    resetForm?: () => void;
    settings: any;
    isBuildingRoute?: boolean;
    isSavingCarTrip?: boolean;
}> = ({
    showAddCarTrip,
    setShowAddCarTrip,
    newCarTrip,
    setNewCarTrip,
    addCarTrip,
    updateCarTrip,
    isEditing = false,
    resetForm,
    settings,
    isBuildingRoute = false,
    isSavingCarTrip = false,
}) => {
    const { t } = useTranslation(settings.language);
    const [activeDayIndex, setActiveDayIndex] = useState(0);
    const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set([0]));
    const [tripTags, setTripTags] = useState<string[]>([]);
    const [invalidFields, setInvalidFields] = useState<InvalidFields>({});
    const [isCalculatingRoute, setIsCalculatingRoute] = useState(false);
    const wasOpenRef = useRef(false);

    const days = useMemo<CarTripDayDraft[]>(() => {
        if (newCarTrip.days?.length) return newCarTrip.days as CarTripDayDraft[];
        return [createDraftDay()];
    }, [newCarTrip.days]);
    useEffect(() => {
        if (!showAddCarTrip) {
            wasOpenRef.current = false;
            return;
        }
        if (wasOpenRef.current) return;
        wasOpenRef.current = true;
        getAllTripTags().then(setTripTags).catch(() => setTripTags([]));
        const lastDayIndex = days.length - 1;
        const lastDay = days[lastDayIndex];
        const initialDayIndex = days.length > 1
            && !lastDay.originCity.trim()
            && !lastDay.destinationCity.trim()
            ? lastDayIndex
            : 0;
        setActiveDayIndex(initialDayIndex);
        setExpandedDays(new Set([initialDayIndex]));
    }, [showAddCarTrip, days]);

    const updateDay = (index: number, patch: Partial<CarTripDayDraft>) => {
        setNewCarTrip((previous) => ({
            ...previous,
            days: updateDraftDay(
                (previous.days?.length ? previous.days : days) as CarTripDayDraft[],
                index,
                patch,
            ) as CarTrip['days'],
        }));
        setInvalidFields((previous) => {
            const next = { ...previous };
            Object.keys(patch).forEach((field) => delete next[`days.${index}.${field}`]);
            return next;
        });
    };

    const addDay = () => {
        const previousDate = days[days.length - 1]?.date;
        const nextDate = previousDate
            ? new Date(`${previousDate}T12:00:00`)
            : new Date();
        nextDate.setDate(nextDate.getDate() + 1);
        const nextDays = addDraftDay(days, nextDate.toISOString().slice(0, 10));
        const nextIndex = nextDays.length - 1;
        setNewCarTrip((previous) => ({ ...previous, days: nextDays as CarTrip['days'] }));
        setActiveDayIndex(nextIndex);
        setExpandedDays((previous) => new Set([...previous, nextIndex]));
    };

    const removeDay = (index: number) => {
        const nextDays = removeDraftDay(days, index);
        setNewCarTrip((previous) => ({ ...previous, days: nextDays as CarTrip['days'] }));
        setActiveDayIndex((previous) => Math.min(previous > index ? previous - 1 : previous, nextDays.length - 1));
        setExpandedDays((previous) => {
            const next = new Set<number>();
            previous.forEach((expandedIndex) => {
                if (expandedIndex < index) next.add(expandedIndex);
                else if (expandedIndex > index) next.add(expandedIndex - 1);
            });
            next.add(Math.min(index, nextDays.length - 1));
            return next;
        });
    };

    const addWaypoint = (index: number) => {
        setNewCarTrip((previous) => {
            const currentDays = previous.days as CarTripDayDraft[];
            return {
                ...previous,
                days: updateDraftDay(currentDays, index, {
                    waypoints: [...(currentDays[index].waypoints ?? []), ''],
                    distance: 0,
                    roadNumbers: [],
                }) as CarTrip['days'],
            };
        });
    };

    const buildActiveRoute = async (index: number) => {
        const routeDay = days[index];
        const points = [
            routeDay.originCity,
            ...(routeDay.waypoints ?? []).filter((waypoint) => waypoint.trim()),
            routeDay.destinationCity,
        ];
        if (points.some((point) => !point.trim()) || points.length < 2) return;

        setIsCalculatingRoute(true);
        try {
            const segments = await Promise.all(points.slice(0, -1).map((point, segmentIndex) => (
                getDistanceByCities(point, points[segmentIndex + 1], settings.language.slice(0, 2))
            )));
            const distance = segments.reduce((total, segment) => total + (Number(segment.distance) || 0), 0);
            const duration = segments.reduce((total, segment) => total + (Number(segment.duration) || 0), 0);
            const tollTotal = (routeDay.tollRoadCosts ?? []).reduce((total, cost) => total + (Number(cost) || 0), 0);
            const fuelUsed = distance * (Number(newCarTrip.fuelConsumption) || 0) / 100;
            updateDay(index, {
                distance: Math.round(distance),
                duration: Math.round(duration),
                formattedDuration: duration > 0
                    ? `~ ${Math.round(duration / 3600)} ${settings.language.startsWith('ru') ? 'ч' : 'hrs'}`
                    : '',
                legDistances: segments.map((segment) => Math.round(Number(segment.distance) || 0)),
                roadNumbers: [...new Set(segments.reduce<string[]>(
                    (roads, segment) => [...roads, ...(segment.roadNumbers ?? [])],
                    [],
                ))],
                fuelUsed,
                totalCost: fuelUsed * (Number(routeDay.fuelPrice) || 0) + tollTotal,
            } as Partial<CarTripDayDraft>);
        } catch (error) {
            console.error('Active day route calculation failed:', error);
        } finally {
            setIsCalculatingRoute(false);
        }
    };

    const validate = () => {
        const invalid: InvalidFields = {};
        if (!newCarTrip.tag?.trim()) invalid.tag = true;
        if (!newCarTrip.carMake?.trim()) invalid.carMake = true;
        if (!newCarTrip.fuelType?.trim()) invalid.fuelType = true;
        days.forEach((day, index) => {
            if (!day.date) invalid[`days.${index}.date`] = true;
            if (!day.originCity.trim()) invalid[`days.${index}.originCity`] = true;
            if (!day.destinationCity.trim()) invalid[`days.${index}.destinationCity`] = true;
        });
        setInvalidFields(invalid);
        const firstInvalidDay = days.findIndex((_, index) => (
            invalid[`days.${index}.date`]
            || invalid[`days.${index}.originCity`]
            || invalid[`days.${index}.destinationCity`]
        ));
        if (firstInvalidDay >= 0) {
            setActiveDayIndex(firstInvalidDay);
            setExpandedDays((previous) => new Set([...previous, firstInvalidDay]));
        }
        return Object.keys(invalid).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;
        if (isEditing && updateCarTrip) updateCarTrip();
        else addCarTrip();
    };

    if (!showAddCarTrip) return null;

    const inputClass = 'w-full h-9 px-2.5 rounded-lg border border-amber-200/70 dark:border-slate-600 bg-white/90 dark:bg-slate-800/80 text-stone-900 dark:text-white text-sm focus:ring-2 focus:ring-amber-500/35';
    const invalidClass = ' border-red-500 ring-2 ring-red-500/25';
    const labelClass = 'block text-[10px] font-semibold uppercase tracking-wide text-stone-500 dark:text-slate-400 mb-1';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/45 dark:bg-black/55 backdrop-blur-md p-3" role="dialog" aria-modal="true" aria-labelledby="car-trip-form-title">
            <div className="w-full max-w-4xl max-h-[min(100dvh-1.5rem,820px)] flex flex-col rounded-2xl overflow-hidden shadow-2xl border border-amber-200/40 dark:border-slate-600/60 bg-amber-50 dark:bg-slate-900">
                <header className="relative shrink-0 px-4 py-3 border-b border-amber-100 dark:border-slate-700 flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white">
                        <Car className="h-4 w-4" />
                    </span>
                    <h2 id="car-trip-form-title" className="font-semibold text-stone-900 dark:text-white">
                        {isEditing ? t.editTrip : t.newTrip}
                    </h2>
                    <button type="button" onClick={resetForm || (() => setShowAddCarTrip(false))} className="ml-auto p-1.5 rounded-lg text-stone-500 hover:bg-white/70" aria-label={t.cancel}>
                        <X className="h-5 w-5" />
                    </button>
                </header>

                <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4 space-y-3">
                    <div className="grid sm:grid-cols-12 gap-2">
                        <div className="sm:col-span-5">
                            <label className={labelClass}>{t.tagSelectOrCreate} *</label>
                            <TagCombobox
                                value={newCarTrip.tag ?? ''}
                                onChange={(tag) => setNewCarTrip((previous) => ({ ...previous, tag }))}
                                tags={tripTags}
                                placeholder={t.tagSelectOrCreate}
                                required
                                invalid={invalidFields.tag}
                                language={settings.language}
                            />
                        </div>
                        <div className="sm:col-span-4">
                            <label className={labelClass}>{t.carBrand} *</label>
                            <MenuSelect
                                value={newCarTrip.carMake ?? ''}
                                placeholder={t.selectCar}
                                onChange={(carMake) => {
                                    const car = settings.cars.find((item: any) => item.make === carMake);
                                    setNewCarTrip((previous) => ({
                                        ...previous,
                                        carMake,
                                        fuelConsumption: car?.fuelConsumption ?? 0,
                                        fuelType: car?.fuelType ?? '',
                                    }));
                                    if (carMake && car?.fuelType) {
                                        setInvalidFields((previous) => ({ ...previous, carMake: false, fuelType: false }));
                                    }
                                }}
                                options={[
                                    { value: '', label: t.selectCar },
                                    ...settings.cars.map((car: any) => ({ value: car.make, label: car.make })),
                                ]}
                                buttonClassName={`${inputClass} flex items-center justify-between${invalidFields.carMake || invalidFields.fuelType ? invalidClass : ''}`}
                            />
                        </div>
                        <div className="sm:col-span-3">
                            <label className={labelClass}>{t.notes}</label>
                            <input className={inputClass} value={newCarTrip.note ?? ''} onChange={(event) => setNewCarTrip((previous) => ({ ...previous, note: event.target.value }))} />
                        </div>
                    </div>

                    <section className="rounded-xl border border-amber-200/80 dark:border-slate-600 bg-white/70 dark:bg-slate-800/50 p-2.5">
                        <div className="flex items-center justify-between gap-2 mb-2">
                            <h3 className="font-semibold text-stone-800 dark:text-white">{t.carTripDays}</h3>
                            <button type="button" onClick={addDay} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100">
                                <Plus className="h-3.5 w-3.5" /> {t.addCarTripDay}
                            </button>
                        </div>

                        <div className="space-y-2">
                            {days.map((day, index) => {
                                const expanded = expandedDays.has(index);
                                return (
                                    <article key={`${index}-${day.date}`} className={`rounded-xl border ${index === activeDayIndex ? 'border-amber-400' : 'border-stone-200 dark:border-slate-600'} bg-white dark:bg-slate-800 overflow-hidden`}>
                                        <div className="flex items-center gap-2 p-2">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setActiveDayIndex(index);
                                                    setExpandedDays((previous) => {
                                                        const next = new Set(previous);
                                                        if (next.has(index)) next.delete(index);
                                                        else next.add(index);
                                                        return next;
                                                    });
                                                }}
                                                className="flex-1 min-w-0 flex items-center gap-2 text-left"
                                            >
                                                {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                                <span className="font-medium">{t.carTripDay} {index + 1}</span>
                                                <span className="text-xs text-stone-500 truncate inline-flex items-center gap-1.5 min-w-0">
                                                  <span className="shrink-0">{day.date}</span>
                                                  <CarRouteSep compact className="inline-flex" />
                                                  <span className="truncate">{day.originCity || '—'} … {day.destinationCity || '—'}</span>
                                                </span>
                                            </button>
                                            {days.length > 1 && (
                                                <button type="button" onClick={() => removeDay(index)} className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg" aria-label={t.remove}>
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>

                                        {expanded && (
                                            <div className="border-t border-stone-100 dark:border-slate-700 p-2.5 space-y-2" onFocus={() => setActiveDayIndex(index)}>
                                                <div className="grid sm:grid-cols-[10rem_1fr_auto_1fr] gap-2 items-end">
                                                    <div>
                                                        <label className={labelClass}>{t.date} *</label>
                                                        <input type="date" value={day.date} onChange={(event) => updateDay(index, { date: event.target.value })} className={`${inputClass}${invalidFields[`days.${index}.date`] ? invalidClass : ''}`} />
                                                    </div>
                                                    <CityInput compact value={day.originCity} onChange={(originCity) => updateDay(index, { originCity, distance: 0 })} label={t.originCity} required language={settings.language} isInvalid={invalidFields[`days.${index}.originCity`]} />
                                                    <div className="hidden sm:flex items-end justify-center pb-2.5 text-stone-400 dark:text-slate-500">
                                                        <ArrowRight className="h-4 w-4" aria-hidden />
                                                    </div>
                                                    <CityInput compact value={day.destinationCity} onChange={(destinationCity) => updateDay(index, { destinationCity, distance: 0 })} label={t.destinationCity} required language={settings.language} isInvalid={invalidFields[`days.${index}.destinationCity`]} />
                                                </div>

                                                {(day.waypoints ?? []).map((waypoint, waypointIndex) => (
                                                    <div key={waypointIndex} className="flex items-end gap-1.5">
                                                        <div className="flex-1">
                                                            <CityInput compact value={waypoint} onChange={(value) => updateDay(index, {
                                                                waypoints: (day.waypoints ?? []).map((item, itemIndex) => itemIndex === waypointIndex ? value : item),
                                                                distance: 0,
                                                            })} label={`${t.waypointCity} ${waypointIndex + 1}`} language={settings.language} />
                                                        </div>
                                                        <button type="button" onClick={() => updateDay(index, { waypoints: (day.waypoints ?? []).filter((_, itemIndex) => itemIndex !== waypointIndex), distance: 0 })} className="p-2 text-red-500 rounded-lg hover:bg-red-50">
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                ))}

                                                <div className="flex flex-wrap items-center gap-2">
                                                    <button type="button" onClick={() => { setActiveDayIndex(index); addWaypoint(index); }} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs border border-amber-200 dark:border-slate-600">
                                                        <Plus className="h-3.5 w-3.5" /> {t.addWaypoint}
                                                    </button>
                                                    <button type="button" onClick={() => { setActiveDayIndex(index); void buildActiveRoute(index); }} disabled={isCalculatingRoute} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs bg-emerald-600 text-white disabled:opacity-50">
                                                        {isCalculatingRoute && index === activeDayIndex && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                                        {t.route}
                                                    </button>
                                                    <Checkbox size="sm" checked={Boolean(day.avoidFerries)} onChange={(avoidFerries) => updateDay(index, { avoidFerries })} label={t.avoidFerries} />
                                                    <Checkbox size="sm" checked={Boolean(day.avoidToll)} onChange={(avoidToll) => updateDay(index, { avoidToll })} label={t.avoidTollRoads} />
                                                </div>

                                                <div className="grid sm:grid-cols-3 gap-2">
                                                    <div>
                                                        <label className={labelClass}>{t.fuelPrice}</label>
                                                        <input type="number" min="0" step="0.01" value={day.fuelPrice || ''} onChange={(event) => updateDay(index, { fuelPrice: Number(event.target.value) || 0 })} className={inputClass} />
                                                    </div>
                                                    <div>
                                                        <label className={labelClass}>{t.tollRoadCost}</label>
                                                        <input type="number" min="0" step="0.01" value={day.tollRoadCosts?.[0] || ''} onChange={(event) => updateDay(index, { tollRoadCosts: [Number(event.target.value) || 0] })} className={inputClass} />
                                                    </div>
                                                    <div>
                                                        <label className={labelClass}>{t.roadNumbers}</label>
                                                        <input value={(day.roadNumbers ?? []).join(', ')} onChange={(event) => updateDay(index, { roadNumbers: event.target.value.split(',').map((road) => road.trim()).filter(Boolean) })} className={inputClass} />
                                                    </div>
                                                </div>

                                                {(day.distance || 0) > 0 && (
                                                    <div className="flex flex-wrap gap-2 text-xs text-stone-600 dark:text-slate-300">
                                                        <span>{day.distance} {t.km}</span>
                                                        {day.formattedDuration && <span>{day.formattedDuration}</span>}
                                                        {(day.totalCost || 0) > 0 && <span>≈ {day.totalCost?.toFixed(2)} {settings.currency}</span>}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </article>
                                );
                            })}
                        </div>
                    </section>
                </div>

                <footer className="shrink-0 p-3 border-t border-amber-100 dark:border-slate-700 bg-white/90 dark:bg-slate-900 flex justify-end">
                    <button type="button" onClick={handleSubmit} disabled={isBuildingRoute || isCalculatingRoute || isSavingCarTrip} className="w-full sm:w-auto min-w-48 inline-flex items-center justify-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 disabled:opacity-60">
                        {(isBuildingRoute || isCalculatingRoute || isSavingCarTrip) && <Loader2 className="h-4 w-4 animate-spin" />}
                        {isEditing ? t.saveChanges : t.addTrip}
                    </button>
                </footer>
            </div>
        </div>
    );
};
